import camelCase from 'lodash-es/camelCase'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { SECTION_NAMES } from '@constants/sectionNames'
import { Arrows, ArrowsButton, BaseContent, Button, Tag, useSlider } from '@fourthwall/components'
import type { LocationState } from '@types'

import { Award } from '../../../../components/Award/Award'
import { useCampaignStatus } from '../../../../hooks/useCampaignStatus'
import type { App } from '../../IntegrationsRecommendation.types'
import * as S from './AppsSlider.styled'

type AppsSliderProps = {
  heading: string
  apps: App[]
}

const ID = 'integrations'

export const AppsSlider = ({ heading, apps }: AppsSliderProps) => {
  const location = useLocation()
  const slider = useSlider()
  const { activeIncentives } = useCampaignStatus()
  const incentive = activeIncentives.find((i) => i.recommendationId === ID)

  const locationState = { from: { label: SECTION_NAMES.home, location } } satisfies LocationState

  return (
    <S.Container {...slider.getContainerProps()}>
      <S.Header>
        <S.HeaderLeft>
          <S.Heading>{heading}</S.Heading>
          {incentive && (
            <Award value={incentive.incentive.bonus.value} currency="USD" text="sample credit" />
          )}
        </S.HeaderLeft>
        <div>
          <Arrows size="small">
            <ArrowsButton direction="prev" {...slider.getPrevButtonProps()} />
            <ArrowsButton direction="next" {...slider.getNextButtonProps()} />
          </Arrows>
        </div>
      </S.Header>
      {apps && (
        <S.Slider {...slider.getSliderProps()}>
          {apps.map((app) => (
            <S.Slide key={camelCase(app.name)} {...slider.getSlideProps()}>
              <S.Thumbnail src={app.thumbnail} alt={`${app.name} thumbnail`} />
              <div>
                <S.Name>{app.name}</S.Name>
                {app.description && (
                  <BaseContent size="small" appearance="secondary">
                    <S.Description>
                      {app.description}
                      {app.docs ? (
                        <>
                          {' '}
                          <a href={app.docs} target="_blank" rel="noreferrer">
                            Learn more
                          </a>
                        </>
                      ) : null}
                    </S.Description>
                  </BaseContent>
                )}
                <S.Buttons>
                  {app.to ? (
                    <Button
                      label={app.connected ? 'Manage' : 'Connect'}
                      to={app.to}
                      state={locationState}
                      analytics={`${camelCase(app.name)}`}
                      fullWidth
                      size="small"
                      leftIcon={
                        !app.connected && app.signet ? <S.Signet src={app.signet} /> : undefined
                      }
                    />
                  ) : (
                    <Tag label="coming soon" />
                  )}
                </S.Buttons>
              </div>
            </S.Slide>
          ))}
        </S.Slider>
      )}
    </S.Container>
  )
}
